import { AfterViewInit, Component, Injectable, Input, OnChanges, ViewChild, inject } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { MatChipsModule } from '@angular/material/chips';
import { MatPaginator, MatPaginatorIntl, MatPaginatorModule } from '@angular/material/paginator';
import { MatSort, MatSortModule, Sort } from '@angular/material/sort';
import { MatTableDataSource, MatTableModule } from '@angular/material/table';
import { TranslateModule, TranslateService } from '@ngx-translate/core';
import { isNumericType } from '../colimind-utils';
import { cellSortValue, formatCellValue, humanizeColumnName, severityChipColor } from '../colimind-table-format';
import type { QueryResultColumn } from '../colimind-types';

type Row = unknown[];

@Injectable()
export class ColimindPaginatorIntl extends MatPaginatorIntl {
  private translate = inject(TranslateService);

  constructor() {
    super();
    this.translate
      .stream(['colimind.table.itemsPerPage', 'colimind.table.nextPage', 'colimind.table.previousPage',
        'colimind.table.firstPage', 'colimind.table.lastPage'])
      .pipe(takeUntilDestroyed())
      .subscribe(labels => {
        this.itemsPerPageLabel = labels['colimind.table.itemsPerPage'];
        this.nextPageLabel = labels['colimind.table.nextPage'];
        this.previousPageLabel = labels['colimind.table.previousPage'];
        this.firstPageLabel = labels['colimind.table.firstPage'];
        this.lastPageLabel = labels['colimind.table.lastPage'];
        this.changes.next();
      });
  }

  override getRangeLabel = (page: number, pageSize: number, length: number): string => {
    const start = length === 0 ? 0 : page * pageSize + 1;
    const end = Math.min((page + 1) * pageSize, length);
    return this.translate.instant('colimind.table.range', { start, end, total: length });
  };
}

@Component({
  selector: 'colimo-data-table',
  standalone: true,
  imports: [MatTableModule, MatSortModule, MatPaginatorModule, MatChipsModule, TranslateModule],
  providers: [{ provide: MatPaginatorIntl, useClass: ColimindPaginatorIntl }],
  templateUrl: './colimind-data-table.component.html',
  styleUrls: ['./colimind-data-table.component.scss'],
})
export class DataTableComponent implements OnChanges, AfterViewInit {
  @Input({ required: true }) columns: QueryResultColumn[] = [];
  @Input({ required: true }) rows: Row[] = [];

  @ViewChild(MatSort) private sort?: MatSort;
  @ViewChild(MatPaginator) private paginator?: MatPaginator;

  readonly pageSize = 10;
  readonly pageSizeOptions = [10, 25, 50];
  readonly dataSource = new MatTableDataSource<Row>([]);

  columnIds: string[] = [];
  labels: string[] = [];
  numeric: boolean[] = [];

  constructor() {
    this.dataSource.sortData = (data, sort) => this.sorted(data, sort);
  }

  get paged(): boolean {
    return this.rows.length > this.pageSize;
  }

  ngOnChanges(): void {
    this.columnIds = this.columns.map((_, i) => String(i));
    this.labels = this.columns.map(c => humanizeColumnName(c.name));
    this.numeric = this.columns.map(c => isNumericType(c));
    this.dataSource.data = this.rows;
  }

  ngAfterViewInit(): void {
    this.dataSource.sort = this.sort ?? null;
    this.dataSource.paginator = this.paginator ?? null;
  }

  cell(row: Row, i: number): string {
    return formatCellValue(this.columns[i], row[i]);
  }

  chipColor(row: Row, i: number): 'warn' | 'accent' | 'primary' | '' {
    return severityChipColor(this.columns[i]?.name ?? '', row[i]);
  }

  // Empty values stay at the end in both directions
  private sorted(data: Row[], sort: MatSort | Sort): Row[] {
    const i = Number(sort.active);
    if (!sort.direction || isNaN(i)) return data;
    const factor = sort.direction === 'asc' ? 1 : -1;
    return [...data].sort((a, b) => {
      const x = cellSortValue(this.columns[i], a[i]);
      const y = cellSortValue(this.columns[i], b[i]);
      if (x === '' || y === '') return x === y ? 0 : x === '' ? 1 : -1;
      return (x < y ? -1 : x > y ? 1 : 0) * factor;
    });
  }
}
